import React, { useState, useEffect } from "react";
import {
  X,
  Car,
  User,
  Phone,
  Building,
  CreditCard,
  MapPin,
  FileText,
  Edit,
} from "lucide-react";
import {
  updateVehicle,
  getAccountDetails,
  updateAccountDetails,
} from "../../api/vehicalsManagementApi";
import { updateDriver } from "../../api/driverApi";
import apiConfig from "../../config";
import CustomSelect from "../shared/CustomSelect";
import { toast } from "../shared/Toast";
import { VEHICLE_TYPES } from "../../constants/vehicleConstants";

const VehicleDetailsModal = ({ isOpen, onClose, vehicle }) => {
  const [vehicleData, setVehicleData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showUpdateVehicleModal, setShowUpdateVehicleModal] = useState(false);
  const [showUpdateOwnerModal, setShowUpdateOwnerModal] = useState(false);
  const [showUpdateBankModal, setShowUpdateBankModal] = useState(false);
  const [showUpdateDriverModal, setShowUpdateDriverModal] = useState(false);
  const [updateForm, setUpdateForm] = useState({});
  const [filePreviews, setFilePreviews] = useState({});

  // Helper function to construct proper image URLs
  const getImageUrl = (imagePath) => {
    // Handle object structure with url property
    if (imagePath && typeof imagePath === "object" && imagePath.url) {
      return imagePath.url;
    }

    // Handle string paths
    if (imagePath && typeof imagePath === "string") {
      const path = String(imagePath);
      if (path.startsWith("http")) {
        return path;
      }
      if (path.startsWith("/")) {
        return `${apiConfig.apiUrl}${path}`;
      }
      return `${apiConfig.apiUrl}/${path}`;
    }

    return null;
  };

  // Helper function to check if image exists and is valid
  const hasValidImage = (driver, ...possibleFields) => {
    for (const field of possibleFields) {
      const imagePath = driver[field];
      // Handle object structure with url property
      if (imagePath && typeof imagePath === "object" && imagePath.url) {
        return true;
      }
      // Handle string paths
      if (
        imagePath &&
        typeof imagePath === "string" &&
        imagePath.trim() !== ""
      ) {
        if (!imagePath.includes("[object") && !imagePath.includes("Object")) {
          return true;
        }
      }
    }
    return false;
  };

  const DocumentThumbnail = ({ url, label }) => {
    const isPdf = typeof url === 'string' && (url.toLowerCase().includes('.pdf') || url.toLowerCase().includes('/raw/'));

    return (
      <div style={{ textAlign: "center" }}>
        <div
          style={{
            width: "120px",
            height: "80px",
            margin: "0 auto 8px",
            borderRadius: "6px",
            overflow: "hidden",
            border: "1px solid #E5E7EB",
            backgroundColor: isPdf ? "#FEF2F2" : "#F9FAFB",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            transition: "all 0.2s",
          }}
          onClick={() => window.open(url, "_blank")}
          title={`Click to view ${label}`}
        >
          {isPdf ? (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" }}>
              <FileText size={28} color="#EF4444" />
              <span style={{ fontSize: "11px", fontWeight: 600, color: "#DC2626" }}>PDF Document</span>
            </div>
          ) : (
            <img
              src={url}
              alt={label}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src =
                  "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTEyIDJDNi40OCAyIDIgNi40OCAyIDEyUzYuNDggMjIgMTIgMjJTMjIgMTcuNTIgMjIgMTJTMTcuNTIgMiAxMiAyWk0xMiAyMEMxNi40MSAyMCAyMCAxNi40MSAyMCAxMlMxNi40MSA0IDEyIDRTNCA3LjU5IDQgMTJTNy41OSAyMCAxMiAyMFoiIGZpbGw9IiM5Q0EzQUYiLz4KPHBhdGggZD0iTTEyIDEyQzE0LjIxIDEyIDE2IDEwLjIxIDE2IDhDMTYgNS43OSAxNC4yMSA0IDEyIDRDOS43OSA0IDggNS43OSA4IDhDOCAxMC4yMSA5Ljc5IDEyIDEyIDEyWk0xMiAxNEM5Ljc5IDE0IDggMTIuMjEgOCAxMEM4IDcuNzkgOS43OSA2IDEyIDZDMTQuMjEgNiAxNiA3Ljc5IDE2IDEwQzE2IDEyLjIxIDE0LjIxIDE0IDEyIDE0WiIgZmlsbD0iIzlDQTNBRiIvPgo8L3N2Zz4=";
              }}
            />
          )}
        </div>
        <p
          style={{
            margin: 0,
            fontSize: "12px",
            color: "#6B7280",
          }}
        >
          {label}
        </p>
      </div>
    );
  };

  useEffect(() => {
    if (vehicle) {
      setVehicleData(vehicle);
    }
  }, [vehicle]);

  // Fetch bank details when modal opens
  useEffect(() => {
    const fetchBankDetails = async () => {
      if (!vehicle || !vehicle.id) return;

      try {
        const response = await getAccountDetails(vehicle.id);
        if (response && response.success && response.data) {
          const bankData = response.data;
          setVehicleData((prev) => ({
            ...prev,
            accountDetailsId: bankData.id,
            accountHolderName: bankData.accountHolderName || bankData.holderName || bankData.name || prev.ownerName,
            accountNumber: bankData.accountNumber,
            bankName: bankData.bankName,
            bankCategory: bankData.bankCategory,
            ifscCode: bankData.ifscCode,
            branch: bankData.branch,
          }));
        }
      } catch (err) {
        // 404 = no bank details yet, that's fine
        if (err.response?.status !== 404) {
          console.error('Error fetching bank details:', err);
        }
      }
    };

    if (isOpen && vehicle) {
      fetchBankDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, vehicle?.id]);


  // Fetch driver details when modal opens
  useEffect(() => {
    const fetchDriverDetails = async () => {
      if (!vehicle || !vehicle.id) return;

      try {
        // Import driverApi here to avoid circular imports
        const { getAllDrivers, getDriverById, getDriverDocuments } =
          await import("../../api/driverApi");
        const response = await getAllDrivers();

        if (response.success && response.data) {
          const drivers = response.data.filter(
            (d) => d.vehicleId === vehicle.id || d.vehicle_id === vehicle.id,
          );

          if (drivers.length > 0) {
            // Fetch detailed driver information for each driver
            const driversWithImages = await Promise.all(
              drivers.map(async (driver) => {
                try {
                  const driverDetailResponse = await getDriverById(driver.id);

                  if (
                    driverDetailResponse.success &&
                    driverDetailResponse.data
                  ) {
                    const detailedDriver = {
                      ...driver,
                      ...driverDetailResponse.data,
                    };

                    // Debug: Log the raw driver data from getDriverById

                    // Since the getDriverById should return all fields including images,
                    // let's check what fields are actually available
                    const allFields = Object.keys(
                      driverDetailResponse.data || {},
                    );
                    const imageFields = allFields.filter(
                      (field) =>
                        field.toLowerCase().includes("image") ||
                        field.toLowerCase().includes("doc") ||
                        field.toLowerCase().includes("pan") ||
                        field.toLowerCase().includes("aadhar") ||
                        field.toLowerCase().includes("license"),
                    );

                    // Store debug info for display
                    detailedDriver._debugInfo = {
                      allFields: allFields,
                      imageFields: imageFields,
                      panAvailable: !!(
                        detailedDriver.panCardImage?.url ||
                        detailedDriver.panImage?.url
                      ),
                      aadharFrontAvailable: !!(
                        detailedDriver.aadharFrontImage?.url ||
                        detailedDriver.aadharFront?.url
                      ),
                      aadharBackAvailable: !!(
                        detailedDriver.aadharBackImage?.url ||
                        detailedDriver.aadharBack?.url
                      ),
                      licenseAvailable: !!(
                        detailedDriver.licenseImage?.url ||
                        detailedDriver.licenseFront?.url
                      ),
                    };

                    // Simple debug output

                    // Try to fetch driver documents using API function first
                    try {
                      const documentsResponse = await getDriverDocuments(
                        driver.id,
                      );
                      if (documentsResponse.success && documentsResponse.data) {
                        // Merge document image fields
                        Object.assign(detailedDriver, documentsResponse.data);
                      }
                    } catch (apiErr) {
                      // Fallback: Try multiple possible endpoints manually
                      const documentEndpoints = [
                        `/driver/documents/${driver.id}`,
                        `/driver/get-documents/${driver.id}`,
                        `/driver/uploads/${driver.id}`,
                        `/driver/files/${driver.id}`,
                        `/uploads/driver/${driver.id}`,
                      ];

                      for (const endpoint of documentEndpoints) {
                        try {
                          const documentsResponse = await fetch(
                            `${apiConfig.apiUrl}${endpoint}`,
                          );
                          if (documentsResponse.ok) {
                            const documentsData =
                              await documentsResponse.json();
                            if (documentsData.success && documentsData.data) {
                              // Merge document image fields
                              Object.assign(detailedDriver, documentsData.data);
                              break; // Stop trying other endpoints if this works
                            }
                          }
                        } catch (docErr) {}
                      }
                    }

                    // Debug: Log driver data to see what image fields we have
                    return detailedDriver;
                  }
                  return driver;
                } catch (err) {
                  return driver;
                }
              }),
            );

            setVehicleData((prev) => ({
              ...prev,
              drivers: driversWithImages,
            }));
          }
        }
      } catch (err) {}
    };

    if (isOpen && vehicle) {
      fetchDriverDetails();
    }
  }, [isOpen, vehicle]);

  const handleUpdateVehicle = () => {
    setUpdateForm({
      vehicleNumber:
        vehicleData.vehicleNumber || vehicleData.registrationNumber,
      vehicleType: vehicleData.vehicleType || vehicleData.vehicleBrand,
      model: vehicleData.model,
      seatingCapacity: vehicleData.seatingCapacity,
      ratePerKm: vehicleData.ratePerKm,
    });
    setShowUpdateVehicleModal(true);
  };

  const handleUpdateOwner = () => {
    setUpdateForm({
      ownerName: vehicleData.ownerName,
      contactNumber:
        vehicleData.contactNumber || vehicleData.ownerContactNumber,
      address: vehicleData.address,
    });
    setShowUpdateOwnerModal(true);
  };

  const handleUpdateBank = () => {
    setUpdateForm({
      accountHolderName:
        vehicleData.accountHolderName ||
        vehicleData.holderName ||
        vehicleData.name ||
        vehicleData.ownerName,
      accountNumber: vehicleData.accountNumber,
      bankName: vehicleData.bankName,
      bankCategory: vehicleData.bankCategory || "APW",
      ifscCode: vehicleData.ifscCode,
      branch: vehicleData.branch,
    });
    setShowUpdateBankModal(true);
  };

  const handleUpdateDriver = () => {
    // Check if drivers exist and get the first driver
    const driver =
      vehicleData.drivers && vehicleData.drivers.length > 0
        ? vehicleData.drivers[0]
        : null;

    if (driver) {
      setUpdateForm({
        driverName: driver.driverName || driver.name || "",
        contactNumber: driver.contactNumber || driver.phone || "",
        address: driver.address || "",
        aadharNumber: driver.aadharNumber || driver.aadhar || "",
        panNumber: driver.panNumber || driver.pan || "",
        licenseNumber: driver.licenseNumber || driver.license || "",
        // Initialize file fields from existing driver data
        panCardImage: driver.panCardImage || driver.panImage || null,
        aadharFrontImage: driver.aadharFrontImage || driver.aadharFront || null,
        aadharBackImage: driver.aadharBackImage || driver.aadharBack || null,
        licenseImage: driver.licenseImage || driver.licenseFront || null,
        licenseBackImage: driver.licenseBackImage || driver.licenseBack || null,
      });
      setShowUpdateDriverModal(true);
    } else {
      // No driver assigned - show empty form
      setUpdateForm({
        driverName: "",
        contactNumber: "",
        address: "",
        aadharNumber: "",
        panNumber: "",
        licenseNumber: "",
        panCardImage: null,
        aadharFrontImage: null,
        aadharBackImage: null,
        licenseImage: null,
        licenseBackImage: null,
      });
      setShowUpdateDriverModal(true);
    }
  };

  const handleFileChange = (field, file) => {
    if (file) {
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setFilePreviews((prev) => ({ ...prev, [field]: previewUrl }));

      // Add file to form data
      setUpdateForm((prev) => ({ ...prev, [field]: file }));
    }
  };

  const handleUpdateVehicleSubmit = async () => {
    try {
      setLoading(true);
      const response = await updateVehicle(vehicleData.id, updateForm);
      if (response.success) {
        toast.success("Vehicle updated successfully!");
        setShowUpdateVehicleModal(false);
        // Update local vehicle data
        setVehicleData((prev) => ({ ...prev, ...updateForm }));
      } else {
        toast.error(response.message || "Failed to update vehicle");
      }
    } catch (err) {
      toast.error("Error updating vehicle: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateOwnerSubmit = async () => {
    try {
      setLoading(true);
      const response = await updateVehicle(vehicleData.id, updateForm);
      if (response.success) {
        toast.success("Owner details updated successfully!");
        setShowUpdateOwnerModal(false);
        setVehicleData((prev) => ({ ...prev, ...updateForm }));
      } else {
        toast.error(response.message || "Failed to update owner details");
      }
    } catch (err) {
      toast.error("Error updating owner details: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateBankSubmit = async () => {
    try {
      setLoading(true);
      const response = await updateAccountDetails(vehicleData.id, updateForm);
      if (response.success) {
        toast.success("Bank details updated successfully!");
        setShowUpdateBankModal(false);
        setVehicleData((prev) => ({ ...prev, ...updateForm }));
      } else {
        toast.error(response.message || "Failed to update bank details");
      }
    } catch (err) {
      toast.error("Error updating bank details: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateDriverSubmit = async () => {
    try {
      setLoading(true);
      const driver = vehicleData.drivers && vehicleData.drivers[0];
      if (driver) {
        // Construct FormData to support both text fields and file uploads correctly
        const formData = new FormData();
        formData.append("driverName", updateForm.driverName || "");
        formData.append("contactNumber", updateForm.contactNumber || "");
        formData.append("address", updateForm.address || "");
        formData.append("aadharNumber", updateForm.aadharNumber || "");
        formData.append("panNumber", updateForm.panNumber || "");
        formData.append("licenseNumber", updateForm.licenseNumber || "");

        if (updateForm.panCardImage instanceof File) {
          formData.append("panImage", updateForm.panCardImage);
        }
        if (updateForm.aadharFrontImage instanceof File) {
          formData.append("aadharFrontImage", updateForm.aadharFrontImage);
        }
        if (updateForm.aadharBackImage instanceof File) {
          formData.append("aadharBackImage", updateForm.aadharBackImage);
        }
        if (updateForm.licenseImage instanceof File) {
          formData.append("licenseImage", updateForm.licenseImage);
        }
        if (updateForm.licenseBackImage instanceof File) {
          formData.append("licenseBackImage", updateForm.licenseBackImage);
        }

        const response = await updateDriver(driver.id, formData);
        if (response.success) {
          toast.success("Driver details updated successfully!");
          setShowUpdateDriverModal(false);
          
          // Re-fetch driver details to ensure state and previews are perfectly synced
          try {
            const { getDriverById } = await import("../../api/driverApi");
            const driverDetailResponse = await getDriverById(driver.id);
            if (driverDetailResponse.success && driverDetailResponse.data) {
              setVehicleData((prev) => ({
                ...prev,
                drivers: prev.drivers.map((d) =>
                  d.id === driver.id ? { ...d, ...driverDetailResponse.data } : d,
                ),
              }));
            }
          } catch (fetchErr) {
            console.error("Error refreshing driver details:", fetchErr);
            // Fallback: update state locally
            setVehicleData((prev) => ({
              ...prev,
              drivers: prev.drivers.map((d) =>
                d.id === driver.id ? { ...d, ...updateForm } : d,
              ),
            }));
          }
        } else {
          toast.error(response.message || "Failed to update driver details");
        }
      }
    } catch (err) {
      toast.error("Error updating driver details: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !vehicleData) return null;

  // Update Modal Component
  const UpdateModal = ({ isOpen, onClose, title, children, onSubmit }) => {
    if (!isOpen) return null;

    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          backgroundColor: "rgba(0,0,0,0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 2000,
        }}
      >
        <div
          style={{
            backgroundColor: "white",
            borderRadius: "12px",
            width: "90%",
            maxWidth: "500px",
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
            boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
          }}
        >
          {/* Header */}
          <div
            style={{
              backgroundColor: "#20B967",
              padding: "20px 24px",
              borderRadius: "12px 12px 0 0",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <h2
              style={{
                margin: 0,
                fontSize: "18px",
                fontWeight: 600,
                color: "white",
              }}
            >
              {title}
            </h2>
            <button
              onClick={onClose}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "32px",
                height: "32px",
                borderRadius: "50%",
              }}
            >
              <X size={20} />
            </button>
          </div>

          {/* Content */}
          <div style={{ padding: "24px", overflowY: "auto", flex: 1 }}>
            {children}
          </div>

          {/* Footer */}
          <div
            style={{
              padding: "16px 24px",
              borderTop: "1px solid #E5E7EB",
              display: "flex",
              justifyContent: "flex-end",
              gap: "12px",
            }}
          >
            <button
              onClick={onClose}
              style={{
                padding: "10px 20px",
                border: "1px solid #D1D5DB",
                borderRadius: "6px",
                backgroundColor: "white",
                color: "#6B7280",
                fontSize: "14px",
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              onClick={onSubmit}
              disabled={loading}
              style={{
                padding: "10px 20px",
                border: "none",
                borderRadius: "6px",
                backgroundColor: loading ? "#9CA3AF" : "#20B967",
                color: "white",
                fontSize: "14px",
                fontWeight: 500,
                cursor: loading ? "not-allowed" : "pointer",
              }}
            >
              {loading ? "Updating..." : "Update"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const InfoSection = ({ title, icon: Icon, children, onUpdate }) => (
    <div style={{ marginBottom: "24px" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "8px",
          marginBottom: "16px",
          paddingBottom: "8px",
          borderBottom: "2px solid #E5E7EB",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Icon size={20} color="#20B967" />
          <h3
            style={{
              margin: 0,
              fontSize: "18px",
              fontWeight: 600,
              color: "#333",
            }}
          >
            {title}
          </h3>
        </div>
        {onUpdate && (
          <button
            onClick={onUpdate}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              padding: "6px 12px",
              border: "1px solid #20B967",
              borderRadius: "6px",
              backgroundColor: "white",
              color: "#20B967",
              fontSize: "12px",
              fontWeight: 500,
              cursor: "pointer",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#20B967";
              e.currentTarget.style.color = "white";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "white";
              e.currentTarget.style.color = "#20B967";
            }}
          >
            <Edit size={14} /> Update
          </button>
        )}
      </div>
      {children}
    </div>
  );

  const InfoRow = ({ label, value, icon: Icon }) => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        marginBottom: "12px",
        padding: "8px 0",
      }}
    >
      {Icon && <Icon size={16} color="#6B7280" style={{ flexShrink: 0 }} />}
      <div
        style={{
          minWidth: "120px",
          fontSize: "14px",
          fontWeight: 500,
          color: "#555",
        }}
      >
        {label}:
      </div>
      <div style={{ fontSize: "14px", color: "#333", flex: 1 }}>
        {value || "N/A"}
      </div>
    </div>
  );

  return (
    <>
      {/* Main Vehicle Details Modal */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          backgroundColor: "rgba(0,0,0,0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000,
        }}
      >
        <div
          style={{
            backgroundColor: "white",
            borderRadius: "12px",
            width: "90%",
            maxWidth: "800px",
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
            boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
          }}
        >
          {/* Header */}
          <div
            style={{
              backgroundColor: "#20B967",
              padding: "20px 24px",
              borderRadius: "12px 12px 0 0",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <Car size={24} color="white" />
              <h2
                style={{
                  margin: 0,
                  fontSize: "20px",
                  fontWeight: 600,
                  color: "white",
                }}
              >
                Vehicle Details
              </h2>
            </div>
            <button
              onClick={onClose}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "32px",
                height: "32px",
                borderRadius: "50%",
                transition: "background-color 0.2s",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor =
                  "rgba(255,255,255,0.1)")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "transparent")
              }
            >
              <X size={20} />
            </button>
          </div>

          {/* Content */}
          <div style={{ padding: "24px", overflowY: "auto", flex: 1 }}>
            {/* Vehicle Details Section */}
            <InfoSection
              title="Vehicle Information"
              icon={Car}
              onUpdate={handleUpdateVehicle}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "16px",
                }}
              >
                <InfoRow
                  label="Vehicle Number"
                  value={
                    vehicleData.vehicleNumber || vehicleData.registrationNumber
                  }
                  icon={Car}
                />
                <InfoRow
                  label="Vehicle Type"
                  value={vehicleData.vehicleType || vehicleData.vehicleBrand}
                  icon={Car}
                />
                <InfoRow label="Model" value={vehicleData.model} icon={Car} />
                <InfoRow
                  label="Seating Capacity"
                  value={vehicleData.seatingCapacity}
                  icon={User}
                />
                <InfoRow
                  label="Rate per KM"
                  value={
                    vehicleData.ratePerKm ? `₹${vehicleData.ratePerKm}` : null
                  }
                  icon={CreditCard}
                />
                <InfoRow
                  label="Status"
                  value={
                    <span
                      style={{
                        padding: "4px 12px",
                        borderRadius: "20px",
                        fontSize: "12px",
                        fontWeight: 600,
                        backgroundColor:
                          vehicleData.status === "Active"
                            ? "#D1FAE5"
                            : "#FEE2E2",
                        color:
                          vehicleData.status === "Active"
                            ? "#065F46"
                            : "#991B1B",
                      }}
                    >
                      {vehicleData.status || "Active"}
                    </span>
                  }
                />
              </div>
            </InfoSection>

            {/* Owner Details Section */}
            <InfoSection
              title="Owner Details"
              icon={User}
              onUpdate={handleUpdateOwner}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "16px",
                }}
              >
                <InfoRow
                  label="Owner Name"
                  value={vehicleData.ownerName}
                  icon={User}
                />
                <InfoRow
                  label="Contact Number"
                  value={
                    vehicleData.contactNumber || vehicleData.ownerContactNumber
                  }
                  icon={Phone}
                />
                <InfoRow
                  label="Address"
                  value={vehicleData.address}
                  icon={MapPin}
                  style={{ gridColumn: "1 / -1" }}
                />
              </div>
            </InfoSection>

            {/* Bank Details Section */}
            <InfoSection
              title="Bank Details"
              icon={CreditCard}
              onUpdate={handleUpdateBank}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "16px",
                }}
              >
                <InfoRow
                  label="Account Number"
                  value={vehicleData.accountNumber}
                  icon={CreditCard}
                />
                <InfoRow
                  label="Bank Name"
                  value={vehicleData.bankName}
                  icon={Building}
                />
                <InfoRow
                  label="IFSC Code"
                  value={vehicleData.ifscCode}
                  icon={FileText}
                />
                <InfoRow
                  label="Branch"
                  value={vehicleData.branch}
                  icon={Building}
                />
              </div>
              {!vehicleData.accountNumber && !vehicleData.bankName && (
                <div
                  style={{
                    textAlign: "center",
                    padding: "20px",
                    color: "#9CA3AF",
                    fontSize: "14px",
                    fontStyle: "italic",
                  }}
                >
                  No bank details available
                </div>
              )}
            </InfoSection>

            {/* Driver Details Section */}
            <InfoSection
              title="Driver Details"
              icon={User}
              onUpdate={handleUpdateDriver}
            >
              {vehicleData.drivers && vehicleData.drivers.length > 0 ? (
                vehicleData.drivers.map((driver, index) => (
                  <div
                    key={index}
                    style={{
                      padding: "16px",
                      backgroundColor: "#F9FAFB",
                      borderRadius: "8px",
                      marginBottom:
                        index < vehicleData.drivers.length - 1 ? "12px" : 0,
                    }}
                  >
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "16px",
                      }}
                    >
                      <InfoRow
                        label="Driver Name"
                        value={driver.driverName || driver.name}
                        icon={User}
                      />
                      <InfoRow
                        label="Contact Number"
                        value={driver.contactNumber || driver.phone}
                        icon={Phone}
                      />
                      <InfoRow
                        label="License Number"
                        value={driver.licenseNumber}
                        icon={FileText}
                      />
                      <InfoRow
                        label="Aadhar Number"
                        value={driver.aadharNumber}
                        icon={FileText}
                      />
                      <InfoRow
                        label="Address"
                        value={driver.address}
                        icon={MapPin}
                        style={{ gridColumn: "1 / -1" }}
                      />
                    </div>

                    {/* Driver Documents Section */}
                    <div
                      style={{
                        marginTop: "16px",
                        paddingTop: "16px",
                        borderTop: "1px solid #E5E7EB",
                      }}
                    >
                      <h4
                        style={{
                          margin: "0 0 12px 0",
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "#374151",
                        }}
                      >
                        Driver Documents
                      </h4>

                      {/* Debug: Show available image fields */}
                      {driver._debugInfo && (
                        <div
                          style={{
                            fontSize: "11px",
                            color: "#333",
                            marginBottom: "12px",
                            padding: "12px",
                            backgroundColor: "#fff3cd",
                            border: "1px solid #ffeaa7",
                            borderRadius: "6px",
                            fontFamily: "monospace",
                          }}
                        >
                          <div
                            style={{
                              fontWeight: "bold",
                              marginBottom: "8px",
                              color: "#856404",
                            }}
                          >
                            🐛 DEBUG INFORMATION
                          </div>
                          <div style={{ marginBottom: "8px" }}>
                            <strong>Document Status:</strong>
                            <br />
                            PAN:{" "}
                            {driver._debugInfo.panAvailable
                              ? "✅ YES"
                              : "❌ NO"}
                            <br />
                            Aadhar Front:{" "}
                            {driver._debugInfo.aadharFrontAvailable
                              ? "✅ YES"
                              : "❌ NO"}
                            <br />
                            Aadhar Back:{" "}
                            {driver._debugInfo.aadharBackAvailable
                              ? "✅ YES"
                              : "❌ NO"}
                            <br />
                            License:{" "}
                            {driver._debugInfo.licenseAvailable
                              ? "✅ YES"
                              : "❌ NO"}
                          </div>
                          <div style={{ marginBottom: "8px" }}>
                            <strong>
                              Potential Image Fields (
                              {driver._debugInfo.imageFields.length}):
                            </strong>
                            <br />
                            {driver._debugInfo.imageFields.length > 0
                              ? driver._debugInfo.imageFields.join(", ")
                              : "None found"}
                          </div>
                          <div>
                            <strong>
                              All API Fields (
                              {driver._debugInfo.allFields.length}):
                            </strong>
                            <br />
                            <div
                              style={{
                                maxHeight: "80px",
                                overflow: "auto",
                                fontSize: "10px",
                                backgroundColor: "#fff",
                                padding: "4px",
                                borderRadius: "3px",
                                border: "1px solid #ddd",
                              }}
                            >
                              {driver._debugInfo.allFields.join(", ")}
                            </div>
                          </div>
                        </div>
                      )}
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fit, minmax(150px, 1fr))",
                          gap: "12px",
                        }}
                      >
                        {/* PAN Card */}
                        {hasValidImage(driver, "panCardImage", "panImage") && (
                          <DocumentThumbnail
                            url={getImageUrl(driver.panCardImage || driver.panImage)}
                            label="PAN Card"
                          />
                        )}

                        {/* Aadhar Front */}
                        {hasValidImage(
                          driver,
                          "aadharFrontImage",
                          "aadharFront",
                        ) && (
                          <DocumentThumbnail
                            url={getImageUrl(driver.aadharFrontImage || driver.aadharFront)}
                            label="Aadhar Front"
                          />
                        )}

                        {/* Aadhar Back */}
                        {hasValidImage(
                          driver,
                          "aadharBackImage",
                          "aadharBack",
                        ) && (
                          <DocumentThumbnail
                            url={getImageUrl(driver.aadharBackImage || driver.aadharBack)}
                            label="Aadhar Back"
                          />
                        )}

                        {/* License Front */}
                        {hasValidImage(
                          driver,
                          "licenseImage",
                          "licenseFront",
                        ) && (
                          <DocumentThumbnail
                            url={getImageUrl(driver.licenseImage || driver.licenseFront)}
                            label="License Front"
                          />
                        )}

                        {/* License Back */}
                        {hasValidImage(
                          driver,
                          "licenseBackImage",
                          "licenseBack",
                        ) && (
                          <DocumentThumbnail
                            url={getImageUrl(driver.licenseBackImage || driver.licenseBack)}
                            label="License Back"
                          />
                        )}
                      </div>

                      {/* Show message if no documents */}
                      {!hasValidImage(
                        driver,
                        "panCardImage",
                        "panImage",
                        "aadharFrontImage",
                        "aadharFront",
                        "aadharBackImage",
                        "aadharBack",
                        "licenseImage",
                        "licenseFront",
                        "licenseBackImage",
                        "licenseBack",
                      ) && (
                        <div
                          style={{
                            textAlign: "center",
                            padding: "16px",
                            color: "#9CA3AF",
                            fontSize: "12px",
                            fontStyle: "italic",
                            backgroundColor: "#F9FAFB",
                            borderRadius: "6px",
                            border: "1px dashed #E5E7EB",
                          }}
                        >
                          No documents uploaded
                        </div>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div
                  style={{
                    textAlign: "center",
                    padding: "20px",
                    color: "#9CA3AF",
                    fontSize: "14px",
                    fontStyle: "italic",
                  }}
                >
                  No driver assigned to this vehicle
                </div>
              )}
            </InfoSection>
          </div>

          {/* Footer */}
          <div
            style={{
              padding: "16px 24px",
              borderTop: "1px solid #E5E7EB",
              display: "flex",
              justifyContent: "flex-end",
              gap: "12px",
            }}
          >
            <button
              onClick={onClose}
              style={{
                padding: "10px 20px",
                border: "1px solid #D1D5DB",
                borderRadius: "6px",
                backgroundColor: "white",
                color: "#6B7280",
                fontSize: "14px",
                fontWeight: 500,
                cursor: "pointer",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#F9FAFB";
                e.currentTarget.style.borderColor = "#9CA3AF";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "white";
                e.currentTarget.style.borderColor = "#D1D5DB";
              }}
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Update Modals */}
      <UpdateModal
        isOpen={showUpdateVehicleModal}
        onClose={() => setShowUpdateVehicleModal(false)}
        title="Vehicle Profile"
        onSubmit={handleUpdateVehicleSubmit}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: 500,
                color: "#374151",
              }}
            >
              Vehicle Number
            </label>
            <input
              type="text"
              value={updateForm.vehicleNumber || ""}
              onChange={(e) =>
                setUpdateForm((prev) => ({
                  ...prev,
                  vehicleNumber: e.target.value,
                }))
              }
              style={{
                width: "100%",
                padding: "10px 12px",
                border: "1px solid #D1D5DB",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            />
          </div>
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: 500,
                color: "#374151",
              }}
            >
              Vehicle Type
            </label>
            <CustomSelect
              value={updateForm.vehicleType || ""}
              onChange={(val) =>
                setUpdateForm((prev) => ({
                  ...prev,
                  vehicleType: val,
                }))
              }
              options={VEHICLE_TYPES}
              placeholder="Select Type"
              width="100%"
            />
          </div>
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: 500,
                color: "#374151",
              }}
            >
              Model
            </label>
            <input
              type="text"
              value={updateForm.model || ""}
              onChange={(e) =>
                setUpdateForm((prev) => ({ ...prev, model: e.target.value }))
              }
              style={{
                width: "100%",
                padding: "10px 12px",
                border: "1px solid #D1D5DB",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            />
          </div>
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: 500,
                color: "#374151",
              }}
            >
              Seating Capacity
            </label>
            <input
              type="number"
              value={updateForm.seatingCapacity || ""}
              onChange={(e) =>
                setUpdateForm((prev) => ({
                  ...prev,
                  seatingCapacity: e.target.value,
                }))
              }
              style={{
                width: "100%",
                padding: "10px 12px",
                border: "1px solid #D1D5DB",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            />
          </div>
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: 500,
                color: "#374151",
              }}
            >
              Rate per KM
            </label>
            <input
              type="number"
              value={updateForm.ratePerKm || ""}
              onChange={(e) =>
                setUpdateForm((prev) => ({
                  ...prev,
                  ratePerKm: e.target.value,
                }))
              }
              style={{
                width: "100%",
                padding: "10px 12px",
                border: "1px solid #D1D5DB",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            />
          </div>
        </div>
      </UpdateModal>

      <UpdateModal
        isOpen={showUpdateOwnerModal}
        onClose={() => setShowUpdateOwnerModal(false)}
        title="Update Owner Details"
        onSubmit={handleUpdateOwnerSubmit}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: 500,
                color: "#374151",
              }}
            >
              Owner Name
            </label>
            <input
              type="text"
              value={updateForm.ownerName || ""}
              onChange={(e) =>
                setUpdateForm((prev) => ({
                  ...prev,
                  ownerName: e.target.value,
                }))
              }
              style={{
                width: "100%",
                padding: "10px 12px",
                border: "1px solid #D1D5DB",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            />
          </div>
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: 500,
                color: "#374151",
              }}
            >
              Contact Number
            </label>
            <input
              type="text"
              value={updateForm.contactNumber || ""}
              onChange={(e) =>
                setUpdateForm((prev) => ({
                  ...prev,
                  contactNumber: e.target.value,
                }))
              }
              style={{
                width: "100%",
                padding: "10px 12px",
                border: "1px solid #D1D5DB",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            />
          </div>
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: 500,
                color: "#374151",
              }}
            >
              Address
            </label>
            <textarea
              value={updateForm.address || ""}
              onChange={(e) =>
                setUpdateForm((prev) => ({ ...prev, address: e.target.value }))
              }
              style={{
                width: "100%",
                padding: "10px 12px",
                border: "1px solid #D1D5DB",
                borderRadius: "6px",
                fontSize: "14px",
                minHeight: "80px",
                resize: "vertical",
              }}
            />
          </div>
        </div>
      </UpdateModal>

      <UpdateModal
        isOpen={showUpdateBankModal}
        onClose={() => setShowUpdateBankModal(false)}
        title="Update Bank Details"
        onSubmit={handleUpdateBankSubmit}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "16px",
          }}
        >
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: 500,
                color: "#374151",
              }}
            >
              Account Holder Name
            </label>
            <input
              type="text"
              value={updateForm.accountHolderName || ""}
              onChange={(e) =>
                setUpdateForm((prev) => ({
                  ...prev,
                  accountHolderName: e.target.value,
                }))
              }
              style={{
                width: "100%",
                padding: "10px 12px",
                border: "1px solid #D1D5DB",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            />
          </div>
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: 500,
                color: "#374151",
              }}
            >
              Account Number
            </label>
            <input
              type="text"
              value={updateForm.accountNumber || ""}
              onChange={(e) =>
                setUpdateForm((prev) => ({
                  ...prev,
                  accountNumber: e.target.value,
                }))
              }
              style={{
                width: "100%",
                padding: "10px 12px",
                border: "1px solid #D1D5DB",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            />
          </div>
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: 500,
                color: "#374151",
              }}
            >
              Bank Name
            </label>
            <input
              type="text"
              value={updateForm.bankName || ""}
              onChange={(e) =>
                setUpdateForm((prev) => ({ ...prev, bankName: e.target.value }))
              }
              style={{
                width: "100%",
                padding: "10px 12px",
                border: "1px solid #D1D5DB",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            />
          </div>
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: 500,
                color: "#374151",
              }}
            >
              Bank Category
            </label>
            <CustomSelect
              value={updateForm.bankCategory || "APW"}
              onChange={(val) =>
                setUpdateForm((prev) => ({ ...prev, bankCategory: val }))
              }
              options={[
                { value: "APW", label: "APW" },
                { value: "APO", label: "APO" },
                { value: "Other", label: "Other" },
              ]}
              width="100%"
            />
          </div>
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: 500,
                color: "#374151",
              }}
            >
              IFSC Code
            </label>
            <input
              type="text"
              value={updateForm.ifscCode || ""}
              onChange={(e) =>
                setUpdateForm((prev) => ({ ...prev, ifscCode: e.target.value }))
              }
              style={{
                width: "100%",
                padding: "10px 12px",
                border: "1px solid #D1D5DB",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            />
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: 500,
                color: "#374151",
              }}
            >
              Branch
            </label>
            <input
              type="text"
              value={updateForm.branch || ""}
              onChange={(e) =>
                setUpdateForm((prev) => ({ ...prev, branch: e.target.value }))
              }
              style={{
                width: "100%",
                padding: "10px 12px",
                border: "1px solid #D1D5DB",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            />
          </div>
        </div>
      </UpdateModal>

      <UpdateModal
        isOpen={showUpdateDriverModal}
        onClose={() => setShowUpdateDriverModal(false)}
        title="Update Driver Details"
        onSubmit={handleUpdateDriverSubmit}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: 500,
                color: "#374151",
              }}
            >
              Driver Name
            </label>
            <input
              type="text"
              value={updateForm.driverName || ""}
              onChange={(e) =>
                setUpdateForm((prev) => ({
                  ...prev,
                  driverName: e.target.value,
                }))
              }
              style={{
                width: "100%",
                padding: "10px 12px",
                border: "1px solid #D1D5DB",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            />
          </div>
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: 500,
                color: "#374151",
              }}
            >
              Contact Number
            </label>
            <input
              type="text"
              value={updateForm.contactNumber || ""}
              onChange={(e) =>
                setUpdateForm((prev) => ({
                  ...prev,
                  contactNumber: e.target.value,
                }))
              }
              style={{
                width: "100%",
                padding: "10px 12px",
                border: "1px solid #D1D5DB",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            />
          </div>
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: 500,
                color: "#374151",
              }}
            >
              Address
            </label>
            <textarea
              value={updateForm.address || ""}
              onChange={(e) =>
                setUpdateForm((prev) => ({ ...prev, address: e.target.value }))
              }
              style={{
                width: "100%",
                padding: "10px 12px",
                border: "1px solid #D1D5DB",
                borderRadius: "6px",
                fontSize: "14px",
                minHeight: "80px",
                resize: "vertical",
              }}
            />
          </div>
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: 500,
                color: "#374151",
              }}
            >
              Aadhar Number
            </label>
            <input
              type="text"
              value={updateForm.aadharNumber || ""}
              onChange={(e) =>
                setUpdateForm((prev) => ({
                  ...prev,
                  aadharNumber: e.target.value,
                }))
              }
              style={{
                width: "100%",
                padding: "10px 12px",
                border: "1px solid #D1D5DB",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            />
          </div>
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: 500,
                color: "#374151",
              }}
            >
              PAN Number
            </label>
            <input
              type="text"
              value={updateForm.panNumber || ""}
              onChange={(e) =>
                setUpdateForm((prev) => ({
                  ...prev,
                  panNumber: e.target.value,
                }))
              }
              style={{
                width: "100%",
                padding: "10px 12px",
                border: "1px solid #D1D5DB",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            />
          </div>
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: 500,
                color: "#374151",
              }}
            >
              License Number
            </label>
            <input
              type="text"
              value={updateForm.licenseNumber || ""}
              onChange={(e) =>
                setUpdateForm((prev) => ({
                  ...prev,
                  licenseNumber: e.target.value,
                }))
              }
              style={{
                width: "100%",
                padding: "10px 12px",
                border: "1px solid #D1D5DB",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            />
          </div>
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: 500,
                color: "#374151",
              }}
            >
              PAN Card (Image or PDF)
            </label>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <input
                type="file"
                accept="image/*,application/pdf,.pdf"
                onChange={(e) =>
                  handleFileChange("panCardImage", e.target.files[0])
                }
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: "1px solid #D1D5DB",
                  borderRadius: "6px",
                  fontSize: "14px",
                }}
              />
              {filePreviews.panCardImage && (
                <div
                  style={{
                    width: "60px",
                    height: "60px",
                    borderRadius: "6px",
                    border: "1px solid #E5E7EB",
                    marginTop: "8px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: (updateForm.panCardImage?.name?.toLowerCase().endsWith(".pdf") || updateForm.panCardImage?.type === "application/pdf") ? "#FEF2F2" : "transparent",
                    overflow: "hidden",
                    flexShrink: 0,
                  }}
                >
                  {updateForm.panCardImage?.name?.toLowerCase().endsWith(".pdf") || updateForm.panCardImage?.type === "application/pdf" ? (
                    <>
                      <FileText size={22} color="#EF4444" />
                      <span style={{ fontSize: "10px", color: "#DC2626", fontWeight: 600 }}>PDF</span>
                    </>
                  ) : (
                    <img
                      src={filePreviews.panCardImage}
                      alt="PAN Card Preview"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  )}
                </div>
              )}
              {updateForm.panCardImage && !filePreviews.panCardImage && (
                <div
                  style={{
                    fontSize: "12px",
                    color: "#6B7280",
                    marginTop: "4px",
                    fontStyle: "italic",
                  }}
                >
                  Current: {updateForm.panCardImage.name || (typeof updateForm.panCardImage === "string" ? "Uploaded File" : "No file selected")}
                </div>
              )}
            </div>
          </div>
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: 500,
                color: "#374151",
              }}
            >
              Aadhar Card Front (Image or PDF)
            </label>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <input
                type="file"
                accept="image/*,application/pdf,.pdf"
                onChange={(e) =>
                  handleFileChange("aadharFrontImage", e.target.files[0])
                }
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: "1px solid #D1D5DB",
                  borderRadius: "6px",
                  fontSize: "14px",
                }}
              />
              {filePreviews.aadharFrontImage && (
                <div
                  style={{
                    width: "60px",
                    height: "60px",
                    borderRadius: "6px",
                    border: "1px solid #E5E7EB",
                    marginTop: "8px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: (updateForm.aadharFrontImage?.name?.toLowerCase().endsWith(".pdf") || updateForm.aadharFrontImage?.type === "application/pdf") ? "#FEF2F2" : "transparent",
                    overflow: "hidden",
                    flexShrink: 0,
                  }}
                >
                  {updateForm.aadharFrontImage?.name?.toLowerCase().endsWith(".pdf") || updateForm.aadharFrontImage?.type === "application/pdf" ? (
                    <>
                      <FileText size={22} color="#EF4444" />
                      <span style={{ fontSize: "10px", color: "#DC2626", fontWeight: 600 }}>PDF</span>
                    </>
                  ) : (
                    <img
                      src={filePreviews.aadharFrontImage}
                      alt="Aadhar Front Preview"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  )}
                </div>
              )}
              {updateForm.aadharFrontImage &&
                !filePreviews.aadharFrontImage && (
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#6B7280",
                      marginTop: "4px",
                      fontStyle: "italic",
                    }}
                  >
                    Current:{" "}
                    {updateForm.aadharFrontImage.name || (typeof updateForm.aadharFrontImage === "string" ? "Uploaded File" : "No file selected")}
                  </div>
                )}
            </div>
          </div>
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: 500,
                color: "#374151",
              }}
            >
              Aadhar Card Back (Image or PDF)
            </label>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <input
                type="file"
                accept="image/*,application/pdf,.pdf"
                onChange={(e) =>
                  handleFileChange("aadharBackImage", e.target.files[0])
                }
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: "1px solid #D1D5DB",
                  borderRadius: "6px",
                  fontSize: "14px",
                }}
              />
              {filePreviews.aadharBackImage && (
                <div
                  style={{
                    width: "60px",
                    height: "60px",
                    borderRadius: "6px",
                    border: "1px solid #E5E7EB",
                    marginTop: "8px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: (updateForm.aadharBackImage?.name?.toLowerCase().endsWith(".pdf") || updateForm.aadharBackImage?.type === "application/pdf") ? "#FEF2F2" : "transparent",
                    overflow: "hidden",
                    flexShrink: 0,
                  }}
                >
                  {updateForm.aadharBackImage?.name?.toLowerCase().endsWith(".pdf") || updateForm.aadharBackImage?.type === "application/pdf" ? (
                    <>
                      <FileText size={22} color="#EF4444" />
                      <span style={{ fontSize: "10px", color: "#DC2626", fontWeight: 600 }}>PDF</span>
                    </>
                  ) : (
                    <img
                      src={filePreviews.aadharBackImage}
                      alt="Aadhar Back Preview"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  )}
                </div>
              )}
              {updateForm.aadharBackImage && !filePreviews.aadharBackImage && (
                <div
                  style={{
                    fontSize: "12px",
                    color: "#6B7280",
                    marginTop: "4px",
                    fontStyle: "italic",
                  }}
                >
                  Current:{" "}
                  {updateForm.aadharBackImage.name || (typeof updateForm.aadharBackImage === "string" ? "Uploaded File" : "No file selected")}
                </div>
              )}
            </div>
          </div>
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: 500,
                color: "#374151",
              }}
            >
              License Front (Image or PDF)
            </label>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <input
                type="file"
                accept="image/*,application/pdf,.pdf"
                onChange={(e) =>
                  handleFileChange("licenseImage", e.target.files[0])
                }
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: "1px solid #D1D5DB",
                  borderRadius: "6px",
                  fontSize: "14px",
                }}
              />
              {filePreviews.licenseImage && (
                <div
                  style={{
                    width: "60px",
                    height: "60px",
                    borderRadius: "6px",
                    border: "1px solid #E5E7EB",
                    marginTop: "8px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: (updateForm.licenseImage?.name?.toLowerCase().endsWith(".pdf") || updateForm.licenseImage?.type === "application/pdf") ? "#FEF2F2" : "transparent",
                    overflow: "hidden",
                    flexShrink: 0,
                  }}
                >
                  {updateForm.licenseImage?.name?.toLowerCase().endsWith(".pdf") || updateForm.licenseImage?.type === "application/pdf" ? (
                    <>
                      <FileText size={22} color="#EF4444" />
                      <span style={{ fontSize: "10px", color: "#DC2626", fontWeight: 600 }}>PDF</span>
                    </>
                  ) : (
                    <img
                      src={filePreviews.licenseImage}
                      alt="License Front Preview"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  )}
                </div>
              )}
              {updateForm.licenseImage && !filePreviews.licenseImage && (
                <div
                  style={{
                    fontSize: "12px",
                    color: "#6B7280",
                    marginTop: "4px",
                    fontStyle: "italic",
                  }}
                >
                  Current:{" "}
                  {updateForm.licenseImage.name || (typeof updateForm.licenseImage === "string" ? "Uploaded File" : "No file selected")}
                </div>
              )}
            </div>
          </div>
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: 500,
                color: "#374151",
              }}
            >
              License Back (Image or PDF)
            </label>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <input
                type="file"
                accept="image/*,application/pdf,.pdf"
                onChange={(e) =>
                  handleFileChange("licenseBackImage", e.target.files[0])
                }
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: "1px solid #D1D5DB",
                  borderRadius: "6px",
                  fontSize: "14px",
                }}
              />
              {filePreviews.licenseBackImage && (
                <div
                  style={{
                    width: "60px",
                    height: "60px",
                    borderRadius: "6px",
                    border: "1px solid #E5E7EB",
                    marginTop: "8px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: (updateForm.licenseBackImage?.name?.toLowerCase().endsWith(".pdf") || updateForm.licenseBackImage?.type === "application/pdf") ? "#FEF2F2" : "transparent",
                    overflow: "hidden",
                    flexShrink: 0,
                  }}
                >
                  {updateForm.licenseBackImage?.name?.toLowerCase().endsWith(".pdf") || updateForm.licenseBackImage?.type === "application/pdf" ? (
                    <>
                      <FileText size={22} color="#EF4444" />
                      <span style={{ fontSize: "10px", color: "#DC2626", fontWeight: 600 }}>PDF</span>
                    </>
                  ) : (
                    <img
                      src={filePreviews.licenseBackImage}
                      alt="License Back Preview"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  )}
                </div>
              )}
              {updateForm.licenseBackImage && !filePreviews.licenseBackImage && (
                <div
                  style={{
                    fontSize: "12px",
                    color: "#6B7280",
                    marginTop: "4px",
                    fontStyle: "italic",
                  }}
                >
                  Current:{" "}
                  {updateForm.licenseBackImage.name || (typeof updateForm.licenseBackImage === "string" ? "Uploaded File" : "No file selected")}
                </div>
              )}
            </div>
          </div>
        </div>
      </UpdateModal>
    </>
  );
};

export default VehicleDetailsModal;
